<template>
  <UChip
    v-bind="forwardedProps"
    :as="ChipRoot"
    v-model:show="show"
    :ui="{
      ...ui,
      base: normalizeClass([standalone && ping ? 'relative' : undefined, ui?.base])
    }"
    class="p-chip select-none"
  >
    <slot />
    <template v-if="ping || icon || $slots.content" #content>
      <span
        v-if="ping"
        aria-hidden="true"
        class="absolute inset-0 animate-ping rounded-full bg-inherit opacity-75"
      />
      <span v-if="$slots.content || text !== undefined || icon" class="relative">
        <slot name="content">
          <FontAwesomeIcon v-if="icon" :icon="icon" aria-hidden="true" />
          <template v-else>{{ text }}</template>
        </slot>
      </span>
    </template>
  </UChip>
</template>

<script setup lang="ts">
import type { ComponentColor } from "@/config/component-colors"
import { defineComponent, h, mergeProps, normalizeClass, useAttrs } from "vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { reactiveOmit } from "@vueuse/core"
import UChip, { type ChipProps, type ChipSlots } from "@nuxt/ui/components/Chip.vue"

type ChipColor = ComponentColor

interface Props extends Omit<ChipProps, "color"> {
  color?: ChipColor
  /** Animate the indicator, respecting reduced motion preferences. */
  ping?: boolean
  /** Optional FontAwesome icon inside the indicator. */
  icon?: [string, string]
}

const props = withDefaults(defineProps<Props>(), { as: "div", color: "secondary" })
const forwardedProps = reactiveOmit(props, "as", "ping", "icon", "ui")

/** Control indicator visibility while keeping the wrapped content mounted. */
const show = defineModel<boolean>("show", { default: true })
defineOptions({ inheritAttrs: false })

defineSlots<ChipSlots>()

const attrs = useAttrs()
// UChip forwards attrs to its child; keep PChip's attrs and events on the wrapper.
const ChipRoot = defineComponent({
  name: "ChipRoot",
  inheritAttrs: false,
  setup(_, { attrs: rootAttrs, slots }) {
    return () =>
      h(
        props.as,
        mergeProps(rootAttrs, attrs),
        typeof props.as === "string" ? slots.default?.() : slots
      )
  }
})
</script>

<style lang="scss" scoped>
.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

@keyframes ping {
  75%,
  100% {
    transform: scale(1.625);
    opacity: 0;
  }
}
</style>
